import { useCallback, useContext, useMemo, useState } from "react";
import { tableHooks } from "components/Table";
import { AssessmentCategoryCell, MultipleChoiceCell, ProsodyCell, RateAndAccuracyCell, RateAndAccuracyHeaderCell, RateAndAccuracyLabelCell, RateAndAccuracyValueCell, ReadingComprehensionCell, ShortGradeCell, StatusCell } from "..";
import { currentDateTime, percent, timeAgoWithOutSeconds } from "utils/formatter";
import { AssessmentsContext } from "../AssessmentsContext";
import { IconButtonGroup } from "components/Form";
import { ReactComponent as VisibilityDisabledIcon } from 'assets/icons/VisibilityDisabled.svg';
import { ReactComponent as VisibilityIcon } from 'assets/icons/Visibility.svg';
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { queryClient as qc, statuses } from 'keys'
import { filter, map } from "lodash";
import { actions } from "store";
import { useDispatch } from "react-redux";
import { app } from "router/routes";
import { useNavigate } from "react-router-dom";
import { ratingListLabel3 } from "keys/rating";
import { ReactComponent as BatteryIcon } from 'assets/icons/Battery.svg';
import { FactoredPercentAccuracyTooltip } from "pages/School/Report";
import { CircularProgress, IconButton, SvgIcon, Tooltip } from "@mui/material";
import { visibilityStatuses } from "keys/visibilityStatuses";
import { features } from 'keys'
import FluencyTooltip from "pages/School/Report/FluencyTooltip";

const useColumns = () => {
    const idCell = tableHooks.useIdCell()
    const testTakerNameCell = tableHooks.useTestTakerNameCell()
    const gradeOfAllocationCell = tableHooks.useGradeOfAllocationCell()
    const playCell = tableHooks.usePlayCell()
    const dispatch = useDispatch()
    const navigate = useNavigate()

    const [currentAssessment, setCurrentAssessment] = useState(null)

    const { setViewReportId, viewCell } = useContext(AssessmentsContext)
    const queryClient = useQueryClient()

    const viewCellComponents = {
        icon: RateAndAccuracyCell,
        label: RateAndAccuracyLabelCell,
        value: RateAndAccuracyValueCell
    }

    const goAssessment = (item) => {
        const { id, multiple_choice_passage_id } = item
        const assessments = queryClient.getQueryData([qc.report])?.data
        const assessmentIds = map(filter(assessments, { status: statuses.inProgress, sub_status: statuses.wordsComplete }), 'id');
        dispatch(actions.setAssessmentIds(assessmentIds))
        // Drawer עדכנו במקום לנווט למסך של הדוח, פותחים כאן באמצעות 
        setViewReportId(id)

        // navigate(`${app.assessments}/${id}`, {
        //     // state: {
        //     //     isPassage: !!multiple_choice_passage_id
        //     // }
        // })
    }

    const columns = useMemo(
        () => [
            idCell,
            {
                accessorKey: 'battery_label',
                header: "סוג",
                enableSorting: false,
                cell: (info) => info.getValue() &&
                    <SvgIcon fontSize="small" component={BatteryIcon} inheritViewBox />
            },
            {
                accessorKey: "visibility_status",
                header: "סטטוס תצוגתי",
                cell: (info) => visibilityStatuses?.[info.getValue()],
                role: "read",
                feature: "visibility-status-cell"
            },
            {
                accessorKey: 'learning_unit_name',
                header: "מסגרת לימודית",
                showBlurred: true,
                showBrokenDisplay: true
            },
            {
                accessorKey: 'reading_assessment_affiliation_name',
                header: "שם בית הספר",
                showBlurred: true
            },
            testTakerNameCell,
            {
                accessorKey: 'reading_assessment_grade_of_execution_name',
                header: "שכבה",
                showBlurred: true,
                cell: ShortGradeCell,
                role: "read",
                feature: "grade-assessments-cell"
            },
            {
                accessorKey: 'reading_assessment_title',
                header: "שם מבדק",
                showBlurred: true
            },
            {
                accessorKey: 'battery_label',
                header: "שם סוללה",
            },
            {
                accessorKey: 'reading_assessment_category_label',
                header: "סוג מבדק",
                showBlurred: true,
                cell: AssessmentCategoryCell
            },
            {
                accessorKey: 'create_date',
                header: "מועד הקצאה",
                cell: info => currentDateTime(info.getValue()),
                enableSorting: false,
                feature: features.create_date_cell,
                role: "read"
            },
            {
                accessorKey: 'reading_assessment_activated_by',
                header: `הופעל ע"י`,
                showBlurred: true,
                feature: "activated-by-cell",
                role: "read",
                showBrokenDisplay: true
            },
            {
                accessorKey: 'sub_status',
                header: "סטטוס תוצאות",
                showBlurred: true,
                cell: StatusCell,
                enableSorting: false
            },
            {
                accessorKey: 'file_uploaded_date',
                header: "מועד ביצוע",
                showBlurred: true,
                cell: info => <div style={{ width: 140 }}>{timeAgoWithOutSeconds(info.getValue())}</div>
            },
            {
                accessorKey: 'calculations.words_correct_per_minute',
                header: RateAndAccuracyHeaderCell,
                alignCenter: true,
                showBlurred: true,
                cell: viewCellComponents[viewCell["calculations.words_correct_per_minute"]],
                meta: {
                    label: "מילים נכונות לדקה",
                    field: "correct_words_per_minute",
                    index: "correct_words_per_minute_rating_index",
                    percent: false
                }
            },
            {
                accessorKey: 'calculations.words_per_minute',
                header: RateAndAccuracyHeaderCell,
                alignCenter: true,
                showBlurred: true,
                cell: viewCellComponents[viewCell["calculations.words_per_minute"]],
                meta: {
                    field: "num_words_per_minute",
                    id: "words_per_minute_rating_id",
                    index: "words_per_minute_rating_index",
                    label: "קצב"
                }
            },
            {
                accessorKey: 'calculations.factored_percent_accuracy',
                header: RateAndAccuracyHeaderCell,
                alignCenter: true,
                showBlurred: true,
                cell: viewCellComponents[viewCell["calculations.factored_percent_accuracy"]],
                meta: {
                    field: "factored_percent_accuracy",
                    id: "factored_percent_accuracy_rating_id",
                    index: "factored_percent_accuracy_rating_index",
                    label: "דיוק (משוקלל)",
                    percent: true,
                    tooltip: FactoredPercentAccuracyTooltip
                }
            },
            {
                accessorKey: 'calculations.percent_accuracy',
                header: RateAndAccuracyHeaderCell,
                alignCenter: true,
                showBlurred: true,
                cell: (cell) => {
                    const CalculationsComponent = viewCellComponents[viewCell["calculations.percent_accuracy"]]
                    return (
                        cell.row.original?.multiple_choice_calculations ?
                            <MultipleChoiceCell {...cell} /> :
                            <CalculationsComponent {...cell} />
                    )
                },
                meta: {
                    field: "percent_accuracy",
                    id: "percent_accuracy_rating_id",
                    index: "percent_accuracy_rating_index",
                    label: "דיוק (גוֺלְמִי)",
                    percent: true
                }
            },
            {
                accessorKey: 'calculations.fluency_reading',
                header: "רצף",
                showBlurred: true,
                header: RateAndAccuracyHeaderCell,
                alignCenter: true,
                cell: viewCellComponents[viewCell["calculations.fluency_reading"]],
                meta: {
                    field: "fluency_reading",
                    index: "fluency_reading_rating_index",
                    percent: true,
                    label: "רצף",
                    ratingLabels: ratingListLabel3,
                    tooltip: FluencyTooltip
                }
            },
            {
                accessorKey: 'prosody',
                header: " פרוזודיה",
                showBlurred: true,
                cell: ProsodyCell,
                enableSorting: false
            },
            {
                accessorKey: "multiple_choice_passage_id",
                header: "הבנת הנקרא",
                alignCenter: true,
                enableSorting: false,
                cell: ReadingComprehensionCell
            },
            {
                accessorKey: 'rating.error_percent',
                header: " טעויות",
                showBlurred: true,
                cell: info => percent(info.getValue())
            },
            {
                accessorKey: 'rating.repetition_percent',
                header: " חזרות",
                showBlurred: true,
                cell: info => percent(info.getValue())
            },
            {
                accessorKey: 'rating.insertion_percent',
                header: "הוספות",
                showBlurred: true,
                cell: info => percent(info.getValue()),
            },
            {
                accessorKey: 'rating.omission_error_and_minor_error_percent',
                header: "השמטות",
                showBlurred: true,
                // enableSorting: false,
                cell: info => percent(info.getValue()),
            },
            {
                accessorKey: 'rating.self_correction_percent',
                header: " תקון עצמי",
                showBlurred: true,
                cell: info => percent(info.getValue()),
            },
            {
                accessorKey: 'rating.minor_error_percent',
                header: " שגיאה קלה",
                showBlurred: true,
                cell: info => percent(info.getValue()),
            },
            {
                accessorKey: 'rating.self_correction_and_minor_error_percent',
                showBlurred: true,
                header: " שגיאה קלה עם תקון עצמי",
                cell: info => percent(info.getValue()),
            },
            playCell,
            {
                accessorKey: 'reports',
                header: "צפיה בדו”ח",
                enableSorting: false,
                cell: ({ row }) => {
                    const { multiple_choice_passage_id, rating, multiple_choice_calculations, id } = row.original || {}

                    const disabled = multiple_choice_calculations || (!rating?.percent_accuracy_rating_id || !rating?.words_per_minute_rating_id)

                    return [
                        <Tooltip title={multiple_choice_calculations ? "במבדקי שאלון הצפייה דרך מבדק האם" : "מעבר לתצוגה מילולית"} placement="left">
                            <div>
                                <IconButton
                                    className="transparent-icon"
                                    disabled={disabled}
                                    onClick={() => {
                                        goAssessment(row.original)
                                    }}
                                >
                                    <SvgIcon component={disabled ? VisibilityDisabledIcon : VisibilityIcon} inheritViewBox />
                                </IconButton>
                            </div>
                        </Tooltip>
                        // <IconButtonGroup
                        //     color="thirdly4"
                        //     key={row.id}
                        //     tooltip="מעבר לתצוגה מילולית"
                        //     icon={disabled ? VisibilityDisabledIcon : VisibilityIcon}
                        //     selected={!disabled}
                        //     disabled={disabled}
                        //     action={() => {
                        //         goAssessment({ ...row.original, id: row.original.id })
                        //     }}
                        // />
                    ];
                }
            }
        ], [viewCell]
    )

    return columns
}

export default useColumns